import React, { useState } from 'react';
import { Button, DatePicker, Space, Tag, Tooltip, message } from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useDispatch, useIntl, useSelector } from 'umi';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { FormatDate, PrettyDate, PrettyDuration } from '@/components/PrettyDate';
import HourSelector from '@/components/Filters/HourSelector';
import {
  editServiceRequestRequest,
  patchCalendarEventRequest,
} from '@/services/api/serviceRequest';
import {
  buildAppointmentTimePayload,
  isServiceRequestStatusEditable,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import { useHasAccess, useServiceRequestPermissions } from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import { getAPIErrorMessage } from '@/utils/request';
import { isRecurringEvent } from '@/utils/recurrence';
import { useRecurrenceScope } from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';
import styles from '@/components/Kanban/ServiceRequests/index.less';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/**
 * The date + start/end rows of the details drawer, with inline editing for
 * both service requests and calendar events (partial PATCH each).
 */
const AppointmentTimeEditor = ({ appointment, onSaved }: {
  appointment: ServiceRequestModel,
  onSaved?: () => void,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  // Drafts are composed in the user's selected calendar timezone, matching
  // every time label in the drawer (FormatDate) and the calendar grid.
  const timezone = useSelector(PreferencesSelectors.selectSelectedTimezone);

  // null = display mode.
  const [draft, setDraft] = useState<{ start: Dayjs, end: Dayjs } | null>(null);
  const [saving, setSaving] = useState(false);

  const hasRescheduleAccess = useHasAccess([PERMISSIONS.ServiceRequests.RESCHEDULE, PERMISSIONS.BUSINESS_PARTNER]);
  const hasCreateForOthers = useHasAccess([PERMISSIONS.CalendarEvents.CREATE_FOR_OTHERS, PERMISSIONS.BUSINESS_PARTNER]);
  const { isOwner, hasServiceRequestActionAll } = useServiceRequestPermissions(appointment);

  // Same gating as the footer's reschedule / edit actions (ServiceRequestAction).
  const isExternal = Boolean(appointment?.isExternal) || appointment?.sync_role === 'external';
  const canEditTime = appointment?.blocker
    ? !isExternal && (isOwner || hasCreateForOthers || hasServiceRequestActionAll)
    : hasRescheduleAccess && isServiceRequestStatusEditable(appointment);

  // utc_start_time / utc_end_time on the processed appointment are Date
  // objects, not the API ISO strings (see processSingleServiceRequest).
  const inTz = (value) => dayjs(new Date(value)).tz(timezone);
  const composeInTz = (day: string, hour: number, minute: number) =>
    dayjs.tz(`${day} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`, timezone);

  const startEditing = () => setDraft({
    start: inTz(appointment.utc_start_time),
    end: inTz(appointment.utc_end_time),
  });

  const handleDayChange = (day: Dayjs) => {
    if (!day || !draft) return;
    const label = day.format('YYYY-MM-DD');
    setDraft({
      start: composeInTz(label, draft.start.hour(), draft.start.minute()),
      end: composeInTz(label, draft.end.hour(), draft.end.minute()),
    });
  };

  const handleTimeChange = (edge: 'start' | 'end') => (time: string) => {
    if (!time || !draft) return;
    const [hour, minute] = time.split(':').map(Number);
    setDraft({ ...draft, [edge]: composeInTz(draft.start.format('YYYY-MM-DD'), hour, minute) });
  };

  const handleSave = async () => {
    if (!draft) return;
    let applyTo;
    if (appointment.blocker && isRecurringEvent(appointment)) {
      const scope = await askRecurrenceScope({ action: 'edit' });
      if (!scope) return;
      applyTo = scope;
    }
    setSaving(true);
    const { params, data } = buildAppointmentTimePayload(appointment, {
      start: draft.start.format('YYYY-MM-DDTHH:mm:ssZ'),
      end: draft.end.format('YYYY-MM-DDTHH:mm:ssZ'),
      timezone,
      applyTo,
    });
    const request = appointment.blocker
      ? patchCalendarEventRequest({ params, data })
      : editServiceRequestRequest({ params, data });

    request.then(({ response, data: responseData }) => {
      setSaving(false);
      if (!response || response.status !== 200 || responseData?.error) {
        return message.error(getAPIErrorMessage(responseData));
      }
      setDraft(null);
      const successMessage = responseData?.message || responseData?.data?.message;
      if (successMessage) message.success(successMessage);
      dispatch({ type: 'serviceRequest/updateFetchCalendarViewAt' });

      // A service-request time edit is a reschedule on the backend: it books a
      // NEW appointment and marks this one `rescheduled`. Follow the new id so
      // the drawer doesn't keep showing the superseded record with old times.
      const rescheduledId = responseData?.data?.serviceRequest?.id;
      if (!appointment.blocker && rescheduledId && rescheduledId !== appointment.id) {
        dispatch({
          type: 'serviceRequest/toggleDetailsDrawer',
          payload: { appointmentId: rescheduledId },
        });
        return;
      }

      // The store keeps processed Date objects under utc_* — never ISO strings.
      dispatch({
        type: 'serviceRequest/updateCalendarEvent',
        payload: {
          event: {
            id: appointment.id,
            utc_start_time: draft.start.toDate(),
            utc_end_time: draft.end.toDate(),
          },
        },
      });
      if (onSaved) onSaved();
    });
  };

  if (draft) {
    return (
      <>
        <Space direction="vertical" size={8} data-testid="appointment-time-editor">
          <Space>
            <CalendarOutlined />
            <DatePicker
              size="small"
              allowClear={false}
              value={draft.start}
              onChange={handleDayChange}
            />
          </Space>
          <Space>
            <ClockCircleOutlined />
            <HourSelector
              value={draft.start.format('HH:mm')}
              onChange={handleTimeChange('start')}
              placeholder="Start time"
            />
            <span className={styles.timeSeparator}>-</span>
            <HourSelector
              value={draft.end.format('HH:mm')}
              from={draft.start.format('HH:mm')}
              onChange={handleTimeChange('end')}
              placeholder="End time"
            />
            <small>
              <Tag bordered={false}>
                <PrettyDuration start={draft.start.toDate()} end={draft.end.toDate()} />
              </Tag>
            </small>
            <Tooltip title={intl.formatMessage({ id: 'common.actions.save', defaultMessage: 'Save' })}>
              <Button
                data-testid="appointment-time-save-btn"
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                loading={saving}
                onClick={handleSave}
              />
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'common.actions.cancel', defaultMessage: 'Cancel' })}>
              <Button
                data-testid="appointment-time-cancel-btn"
                size="small"
                icon={<CloseOutlined />}
                disabled={saving}
                onClick={() => setDraft(null)}
              />
            </Tooltip>
          </Space>
        </Space>
        {recurrenceScopeDrawer}
      </>
    );
  }

  return (
    <Space direction="vertical" size={8}>
      <Space>
        <CalendarOutlined />
        <PrettyDate date={appointment?.utc_start_time} />
        <small>
          <FormatDate
            date={appointment?.utc_start_time}
            format={'dddd, Do MMMM YYYY'}
          />
        </small>
      </Space>
      <Space>
        <ClockCircleOutlined />
        <FormatDate date={appointment?.utc_start_time} format="HH:mm" />
        <span className={styles.timeSeparator}>-</span>
        <FormatDate date={appointment?.utc_end_time} format="HH:mm" />
        <small>
          <Tag bordered={false}>
            <PrettyDuration
              start={appointment?.utc_start_time}
              end={appointment?.utc_end_time}
            />
          </Tag>
        </small>
        {canEditTime && (
          <Tooltip title={intl.formatMessage({ id: 'pages.calendar.timeEdit.adjustTimes', defaultMessage: 'Adjust start & end times' })}>
            <Button
              data-testid="edit-appointment-time-btn"
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={startEditing}
            />
          </Tooltip>
        )}
      </Space>
    </Space>
  );
};

export default AppointmentTimeEditor;
